import { Faq } from "@cryptors/api-client";
import { Box, TextField } from "@mui/material";
import { HttpError, IResourceComponentsProps } from "@refinedev/core";
import { Edit } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import { useEffect } from "react";
import { Controller } from "react-hook-form";

import i18n from "@/i18n";
import { getTranslation } from "@/utils/translation";

import FaqQuestions, { FaqQuestion } from "./FaqQuestions";

interface FaqFormValues {
  id?: number;
  name?: string;
  nameTranslation?: { value: string };
  faqQuestions: FaqQuestion[];
}

export const FaqEdit: React.FC<IResourceComponentsProps> = () => {
  const {
    control,
    refineCore: { formLoading, onFinish, query },
    register,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    reset,
  } = useForm<Faq, HttpError, FaqFormValues>();

  const onFinishHandler = (data: FaqFormValues) => {
    onFinish({
      nameTranslation: { value: data.name ?? "" },
      faqQuestions: data.faqQuestions.map((faqQuestion) => ({
        questionTranslation: { value: faqQuestion.question ?? "" },
        answerTranslation: { value: faqQuestion.answer ?? "" },
      })),
    });
  };

  const data = query?.data?.data as Faq;

  useEffect(() => {
    if (!data) return;
    const lang = i18n.language;
    reset({
      id: data.id,
      name:
        getTranslation(data.nameTranslation, lang)?.value ??
        getTranslation(data.nameTranslation, "fr")?.value ??
        "",
      faqQuestions: data.faqQuestions.map((faqQuestion) => ({
        id: faqQuestion.id,
        question:
          getTranslation(faqQuestion.questionTranslation, lang)?.value ??
          getTranslation(faqQuestion.questionTranslation, "fr")?.value ??
          "",
        answer:
          getTranslation(faqQuestion.answerTranslation, lang)?.value ??
          getTranslation(faqQuestion.answerTranslation, "fr")?.value ??
          "",
      })),
    });
  }, [data, reset]);

  register("faqQuestions");

  return (
    <Edit
      isLoading={formLoading}
      saveButtonProps={{
        onClick: handleSubmit(onFinishHandler),
        disabled: !isDirty || !isValid,
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <TextField
          {...register("id", {
            required: "Ce champ est requis",
            valueAsNumber: true,
          })}
          error={!!errors.id}
          helperText={errors.id?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="number"
          label="Id"
          name="id"
          disabled
        />
        <TextField
          {...register("name", {
            required: "Ce champ est requis",
          })}
          error={!!errors.name}
          helperText={errors.name?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Nom"
          name="name"
        />
        <Controller
          control={control}
          name="faqQuestions"
          defaultValue={[]}
          rules={{
            required: "Ce champ est requis",
            validate: (value: FaqQuestion[]) =>
              value.every((el) => el.answer && el.question),
          }}
          render={({ field }) => (
            <FaqQuestions
              faqQuestions={field.value}
              setValue={(value) => field.onChange(value)}
            />
          )}
        />
      </Box>
    </Edit>
  );
};
